import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { Team, CreateSinglesTeamRequest, CreatePairTeamRequest } from '@sulis-liiga/common';
import teamService from '../../services/teamService';

// Define the state interface
interface TeamState {
  teams: Team[];
  myTeams: Team[];
  partnerRequests: Team[];
  currentTeam: Team | null;
  loading: boolean;
  error: string | null;
  createSuccess: boolean;
}

// Initial state
const initialState: TeamState = {
  teams: [],
  myTeams: [],
  partnerRequests: [],
  currentTeam: null,
  loading: false,
  error: null,
  createSuccess: false,
};

// ── Async thunks ──────────────────────────────────────────────────────────────

export const fetchTeams = createAsyncThunk('team/fetchTeams', async (_, { rejectWithValue }) => {
  try {
    return await teamService.getAllTeams();
  } catch (error) {
    const msg = error instanceof Error ? error.message : 'Failed to fetch teams. Please try again.';
    return rejectWithValue(msg);
  }
});

export const fetchTeamById = createAsyncThunk(
  'team/fetchTeamById',
  async (teamId: string, { rejectWithValue }) => {
    try {
      return await teamService.getTeamById(teamId);
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : 'Failed to fetch team. Please try again.';
      return rejectWithValue(msg);
    }
  },
);

export const createSinglesTeam = createAsyncThunk(
  'team/createSinglesTeam',
  async (req: CreateSinglesTeamRequest | undefined, { rejectWithValue }) => {
    try {
      return await teamService.createSinglesTeam(req);
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : 'Failed to create singles team. Please try again.';
      return rejectWithValue(msg);
    }
  },
);

export const createPairTeam = createAsyncThunk(
  'team/createPairTeam',
  async (req: CreatePairTeamRequest, { rejectWithValue }) => {
    try {
      return await teamService.createPairTeam(req);
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : 'Failed to create pair team. Please try again.';
      return rejectWithValue(msg);
    }
  },
);

export const fetchMyTeams = createAsyncThunk(
  'team/fetchMyTeams',
  async (_, { rejectWithValue }) => {
    try {
      return await teamService.getMyTeams();
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : 'Failed to fetch your teams. Please try again.';
      return rejectWithValue(msg);
    }
  },
);

export const fetchPartnerRequests = createAsyncThunk(
  'team/fetchPartnerRequests',
  async (_, { rejectWithValue }) => {
    try {
      return await teamService.getPartnerRequests();
    } catch (error) {
      const msg =
        error instanceof Error
          ? error.message
          : 'Failed to fetch partner requests. Please try again.';
      return rejectWithValue(msg);
    }
  },
);

export const acceptPartnerRequest = createAsyncThunk(
  'team/acceptPartnerRequest',
  async (teamId: string, { rejectWithValue }) => {
    try {
      return await teamService.acceptPartnerRequest(teamId);
    } catch (error) {
      const msg =
        error instanceof Error
          ? error.message
          : 'Failed to accept partner request. Please try again.';
      return rejectWithValue(msg);
    }
  },
);

export const declinePartnerRequest = createAsyncThunk(
  'team/declinePartnerRequest',
  async (teamId: string, { rejectWithValue }) => {
    try {
      return await teamService.declinePartnerRequest(teamId);
    } catch (error) {
      const msg =
        error instanceof Error
          ? error.message
          : 'Failed to decline partner request. Please try again.';
      return rejectWithValue(msg);
    }
  },
);

export const withdrawTeam = createAsyncThunk(
  'team/withdrawTeam',
  async (teamId: string, { rejectWithValue }) => {
    try {
      return await teamService.withdrawTeam(teamId);
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : 'Failed to withdraw team. Please try again.';
      return rejectWithValue(msg);
    }
  },
);

export const deleteTeam = createAsyncThunk(
  'team/deleteTeam',
  async (teamId: string, { rejectWithValue }) => {
    try {
      const result = await teamService.deleteTeam(teamId);
      // Ensure the deleted team's id is available to the reducer regardless of the
      // backend's hard/soft mode.
      return { id: result?.id ?? teamId };
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : 'Failed to delete team. Please try again.';
      return rejectWithValue(msg);
    }
  },
);

export const transferCaptaincy = createAsyncThunk(
  'team/transferCaptaincy',
  async (
    { teamId, newCaptainUserId }: { teamId: string; newCaptainUserId: string },
    { rejectWithValue },
  ) => {
    try {
      return await teamService.transferCaptaincy(teamId, newCaptainUserId);
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : 'Failed to transfer captaincy. Please try again.';
      return rejectWithValue(msg);
    }
  },
);

export const fetchLeagueTeams = createAsyncThunk(
  'team/fetchLeagueTeams',
  async (
    {
      leagueId,
      category,
      divisionId,
      q,
    }: { leagueId: string; category?: string; divisionId?: string; q?: string },
    { rejectWithValue },
  ) => {
    try {
      return await teamService.getLeagueTeams(leagueId, { category, divisionId, q });
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : 'Failed to fetch league teams. Please try again.';
      return rejectWithValue(msg);
    }
  },
);

// ── Slice ─────────────────────────────────────────────────────────────────────

const teamSlice = createSlice({
  name: 'team',
  initialState,
  reducers: {
    clearTeamError: state => {
      state.error = null;
    },
    clearTeamSuccess: state => {
      state.createSuccess = false;
    },
    setCurrentTeam: (state, action: PayloadAction<Team | null>) => {
      state.currentTeam = action.payload;
    },
  },
  extraReducers: builder => {
    // fetchTeams
    builder.addCase(fetchTeams.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchTeams.fulfilled, (state, action) => {
      state.loading = false;
      state.teams = action.payload ?? [];
    });
    builder.addCase(fetchTeams.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // fetchTeamById
    builder.addCase(fetchTeamById.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchTeamById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentTeam = action.payload;
    });
    builder.addCase(fetchTeamById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // createSinglesTeam
    builder.addCase(createSinglesTeam.pending, state => {
      state.loading = true;
      state.error = null;
      state.createSuccess = false;
    });
    builder.addCase(createSinglesTeam.fulfilled, (state, action) => {
      state.loading = false;
      state.myTeams.push(action.payload);
      state.currentTeam = action.payload;
      state.createSuccess = true;
    });
    builder.addCase(createSinglesTeam.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
      state.createSuccess = false;
    });

    // createPairTeam
    builder.addCase(createPairTeam.pending, state => {
      state.loading = true;
      state.error = null;
      state.createSuccess = false;
    });
    builder.addCase(createPairTeam.fulfilled, (state, action) => {
      state.loading = false;
      state.myTeams.push(action.payload);
      state.currentTeam = action.payload;
      state.createSuccess = true;
    });
    builder.addCase(createPairTeam.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
      state.createSuccess = false;
    });

    // fetchMyTeams
    builder.addCase(fetchMyTeams.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchMyTeams.fulfilled, (state, action) => {
      state.loading = false;
      state.myTeams = action.payload ?? [];
    });
    builder.addCase(fetchMyTeams.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // fetchPartnerRequests
    builder.addCase(fetchPartnerRequests.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchPartnerRequests.fulfilled, (state, action) => {
      state.loading = false;
      state.partnerRequests = action.payload ?? [];
    });
    builder.addCase(fetchPartnerRequests.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // acceptPartnerRequest
    builder.addCase(acceptPartnerRequest.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(acceptPartnerRequest.fulfilled, (state, action) => {
      state.loading = false;
      state.partnerRequests = state.partnerRequests.filter(t => t.id !== action.payload.id);
      // Upsert (replace-by-id, else add) so the accepted team never appears twice
      // in "My teams".
      const idx = state.myTeams.findIndex(t => t.id === action.payload.id);
      if (idx >= 0) state.myTeams[idx] = action.payload;
      else state.myTeams.push(action.payload);
    });
    builder.addCase(acceptPartnerRequest.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // declinePartnerRequest
    builder.addCase(declinePartnerRequest.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(declinePartnerRequest.fulfilled, (state, action) => {
      state.loading = false;
      state.partnerRequests = state.partnerRequests.filter(t => t.id !== action.payload.id);
    });
    builder.addCase(declinePartnerRequest.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // withdrawTeam
    builder.addCase(withdrawTeam.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(withdrawTeam.fulfilled, (state, action) => {
      state.loading = false;
      state.myTeams = state.myTeams.map(t => (t.id === action.payload.id ? action.payload : t));
      if (state.currentTeam?.id === action.payload.id) {
        state.currentTeam = action.payload;
      }
    });
    builder.addCase(withdrawTeam.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // deleteTeam — permanent: remove the team from all local collections
    builder.addCase(deleteTeam.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteTeam.fulfilled, (state, action) => {
      state.loading = false;
      state.myTeams = state.myTeams.filter(t => t.id !== action.payload.id);
      state.teams = state.teams.filter(t => t.id !== action.payload.id);
      if (state.currentTeam?.id === action.payload.id) {
        state.currentTeam = null;
      }
    });
    builder.addCase(deleteTeam.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // transferCaptaincy
    builder.addCase(transferCaptaincy.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(transferCaptaincy.fulfilled, (state, action) => {
      state.loading = false;
      state.currentTeam = action.payload;
      state.myTeams = state.myTeams.map(t => (t.id === action.payload.id ? action.payload : t));
    });
    builder.addCase(transferCaptaincy.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // fetchLeagueTeams
    builder.addCase(fetchLeagueTeams.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchLeagueTeams.fulfilled, (state, action) => {
      state.loading = false;
      state.teams = action.payload ?? [];
    });
    builder.addCase(fetchLeagueTeams.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { clearTeamError, clearTeamSuccess, setCurrentTeam } = teamSlice.actions;
export default teamSlice.reducer;
